import React from "react";
import {
  Dialog,
  Box,
  Stack,
  Avatar,
  Typography,
  IconButton,
  Button,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import EventIcon from "@mui/icons-material/Event";
import PaymentsIcon from "@mui/icons-material/Payments";
import SmsIcon from "@mui/icons-material/Sms";
import {
  leadEmailOptIn,
  leadEmailOptInLabel,
  leadTextsOptIn,
  leadTextsOptInLabel,
} from "../../lib/leadSmsOptIn";
import PersonStat from "../clients/PersonStat";
import PersonPreferences from "../clients/PersonPreferences";
import { buildReadout } from "../clients/verificationReadout";
import VerificationCheckRow from "../clients/VerificationCheckRow";

import {
  type DashboardLeadRow,
  formatLeadMoveInDate,
  formatLeadTimeSince,
} from "../../lib/dashboardLeads";
import { buildLeadDisplayName } from "../../lib/leadPreferences";

function budgetLabel(lead: DashboardLeadRow): string {
  const text = (lead.budget || "").trim();
  if (text) return text;
  if (typeof lead.budget_number === "number" && Number.isFinite(lead.budget_number)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(lead.budget_number);
  }
  return "—";
}

const labelSx = {
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 0.75,
} as const;

export interface LeadDetailModalProps {
  lead: DashboardLeadRow | null;
  open: boolean;
  onClose: () => void;
  onConvert: (lead: DashboardLeadRow) => void | Promise<void>;
  canConvert?: boolean;
}

/**
 * Compact, read-only detail for a New Lead, shown as an overlay on the
 * dashboard. The lead is immutable — this presents what the renter submitted
 * and offers the one state-changing action: Convert to Client.
 */
const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
  lead,
  open,
  onClose,
  onConvert,
  canConvert = true,
}) => {
  const theme = useTheme();

  const name = lead ? buildLeadDisplayName(lead) : "";
  const displayName = !name || name === "Unknown" ? "Unnamed lead" : name;
  const initial = (displayName[0] || "?").toUpperCase();
  const moveIn = lead ? formatLeadMoveInDate(lead.move_in_date) : "";
  const readout = lead ? buildReadout(lead) : [];


  const contactRow = (icon: React.ReactNode, value: string, href?: string) => (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      component={href ? "a" : "div"}
      {...(href ? { href } : {})}
      sx={{
        py: 0.75,
        color: "text.primary",
        textDecoration: "none",
        "& .lead-ic": { color: "primary.main", display: "flex" },
        ...(href ? { "&:hover .lead-val": { color: "primary.main" } } : {}),
      }}
    >
      <Box className="lead-ic">{icon}</Box>
      <Typography className="lead-val" variant="body2" fontWeight={600} noWrap>
        {value}
      </Typography>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, m: 2, maxWidth: 400 } }}
    >
      {!lead ? null : (
        <>
          {/* Header */}
          <Box
            sx={{
              position: "relative",
              px: 2.5,
              pt: 2.25,
              pb: 1.75,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <IconButton
              onClick={onClose}
              aria-label="Close"
              size="small"
              sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 3 }}>
              <Avatar
                sx={{
                  width: 42,
                  height: 42,
                  fontWeight: 800,
                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                  color: "primary.main",
                }}
              >
                {initial}
              </Avatar>
              <Box minWidth={0}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: "primary.main",
                  }}
                >
                  New lead
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} noWrap sx={{ lineHeight: 1.2 }}>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  Received {formatLeadTimeSince(lead.created_at)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Body — read-only, sized to content */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              maxHeight: "64vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1.25}>
              <PersonStat icon={<PaymentsIcon sx={{ fontSize: 15 }} />} label={"Budget"} value={budgetLabel(lead)} />
              <PersonStat icon={<EventIcon sx={{ fontSize: 15 }} />} label={"Move-in"} value={moveIn || "Flexible"} />
            </Stack>

            <Box>
              <Typography sx={labelSx}>Contact</Typography>
              <Stack divider={<Divider flexItem />}>
                {contactRow(
                  <MailOutlineIcon sx={{ fontSize: 17 }} />,
                  lead.email || "No email provided",
                  lead.email ? `mailto:${lead.email}` : undefined
                )}
                {contactRow(
                  <PhoneIcon sx={{ fontSize: 17 }} />,
                  lead.phone || "No phone provided",
                  lead.phone ? `tel:${lead.phone}` : undefined
                )}
                {/* Ch78 · the form's SMS tick, in the operator's words. "no"
                    is "not asked", never an opt-out; unknown says nothing. */}
                {leadTextsOptIn(lead) !== "unknown" && (
                  <Box data-testid="lead-detail-texts">
                    {contactRow(
                      <SmsIcon sx={{ fontSize: 17 }} />,
                      leadTextsOptInLabel(leadTextsOptIn(lead))
                    )}
                  </Box>
                )}
                {/* The form's OTHER consent box. The page could say who you may
                    TEXT and was silent about who you may EMAIL — a question with
                    the same legal shape and the same answer sitting one column
                    away in the same row. */}
                {leadEmailOptIn(lead) !== "unknown" && (
                  <Box data-testid="lead-detail-email-list">
                    {contactRow(
                      <MailOutlineIcon sx={{ fontSize: 17 }} />,
                      leadEmailOptInLabel(leadEmailOptIn(lead))
                    )}
                  </Box>
                )}
              </Stack>
            </Box>

            {/* PKT-0653 · one rendering of "what they want", shared with the
                client record. Both modals drew this chip row from the same two
                helpers in near-identical JSX. */}
            <PersonPreferences
              neighborhoods={lead.neighborhoods}
              neighborhoodOther={lead.neighborhood_other}
              pets={lead.pets}
              heading="Looking for"
              headingSx={labelSx}
            />
          </Box>

          {/* WHY the scorer said what it said.
              The work list puts a verdict on this person ("Ultra fake") and a next
              action beside it ("Archive"). This modal is where the operator lands to
              decide — and it showed the verdict's EVIDENCE nowhere: not the reasons,
              not the flags, not even the label. buildReadout() and VerificationCheckRow
              already existed and were already tested; they rendered only in the CLIENT
              dialog, which is the one place that archive decision never gets made.
              Being asked to discard somebody on a judgement whose reasoning is one
              field away is how a scorer stops being questioned. */}
          <Box sx={{ mt: 2 }} data-testid="lead-verification-readout">
            <Typography
              variant="caption"
              sx={{ letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700 }}
              color="text.secondary"
            >
              Why this score
            </Typography>
            {readout.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Not verified yet — the checks run when an admin clicks Verify on the lead.
              </Typography>
            ) : (
              <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                {readout.map((row) => (
                  <VerificationCheckRow key={row.id} row={row} />
                ))}
                {lead.verification_checked_at && (
                  <Typography variant="caption" color="text.secondary">
                    Checked {new Date(lead.verification_checked_at).toLocaleString()}
                    {lead.verification_provider ? ` \u00b7 ${lead.verification_provider}` : ""}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Button
              onClick={onClose}
              size="small"
              sx={{ color: "text.secondary", fontWeight: 700, flex: "0 0 auto" }}
            >
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            {canConvert && (
              <Button
                onClick={() => onConvert(lead)}
                variant="contained"
                startIcon={<PersonAddIcon />}
                sx={{ fontWeight: 800, borderRadius: 2, px: 2.5, whiteSpace: "nowrap" }}
              >
                Convert to Client
              </Button>
            )}
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default LeadDetailModal;
